import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
    Button,
    Cascader,
    Checkbox,
    Divider,
    Form,
    Input,
    Select,
    Spin,
    Tag,
} from 'antd';
import FormItem from 'antd/es/form/FormItem';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDsInquiry from '../hook/useDsInquiry';
import { ROUTER } from '../../../constants/router';
import { useNavigate } from 'react-router-dom';
import useQuery from '../../../util/useQuery';
import classNames from 'classnames';
import { TAB } from '..';
import { PERSONAL_TYPE } from './Tab3';

const CheckboxGroup = Checkbox.Group;

const COLOR_TYPE = {
    PersonalData: '#1890FF',
    SensitiveData: '#F0536F',
    general: '#4ACE8F',
};

export default function FilterPersonal({ departmentId }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const optionSortby = [
        {
            value: 'categoryPersonalDataName',
            label: t('Personal Data Name'),
            children: [
                {
                    value: 'ASC',
                    label: t('Ascending'),
                },
                {
                    value: 'DESC',
                    label: t('Descending'),
                },
            ],
        },
    ];

    const personalDataTypesInit = [
        {
            label: t('All types of personal data'),
            value: 'all',
        },
        {
            label: t('Personal Data'),
            value: 'Personal Data',
        },
        {
            label: t('Sensitive Data'),
            value: 'Sensitive Data',
        },
    ];

    const [personalDataTypes, setPersonalDataTypes] = useState(
        personalDataTypesInit
    );

    const {
        getCategoryPersonalData,
        exportDataFlowRopa,
        loadingExport,
        dataSubject,
        loading,
    } = useDsInquiry(() => {});

    const [values, setValues] = useState({
        sortby: ['categoryPersonalDataName', 'ASC'],
        search: '',
        personalType: 'all',
    });
    const [checkedList, setCheckedList] = useState([]);
    const [indeterminate, setIndeterminate] = useState(false);
    const [checkAll, setCheckAll] = useState(false);

    const [initUrl, setInitUrl] = useState(true);

    const onChange = (list) => {
        setCheckedList(list);
        setIndeterminate(
            !!list.length && list.length < filterDataSubject(dataSubject).length
        );
        setCheckAll(list.length === filterDataSubject(dataSubject).length);
    };

    const onCheckAllChange = (e) => {
        setCheckedList(
            e.target.checked
                ? filterDataSubject(dataSubject)?.map(
                      (item) => item?.categoryPersonalDataId
                  )
                : []
        );
        setIndeterminate(false);
        setCheckAll(e.target.checked);
    };

    const [form] = Form.useForm();

    useEffect(() => {
        getCategoryPersonalData();
    }, [getCategoryPersonalData]);

    const params = useQuery();

    // Sort categoryPersonalDataName  in dataSubject with character
    const sortDataSubject = useCallback(
        (dataSubject) => {
            const [sortby, order] = values.sortby || [];

            // Filter by personalType
            if (values?.personalType !== 'all') {
                // general not equal to Personal Data and Sensitive Data
                if (values?.personalType === 'general') {
                    dataSubject = dataSubject.filter(
                        (item) =>
                            item?.categoryPersonalDataType !==
                                'Personal Data' &&
                            item?.categoryPersonalDataType !== 'Sensitive Data'
                    );
                } else {
                    dataSubject = dataSubject.filter(
                        (item) =>
                            item?.categoryPersonalDataType ===
                            values?.personalType
                    );
                }
            }

            return dataSubject.sort((a, b) => {
                if (a[sortby] < b[sortby]) {
                    return order === 'ASC' ? -1 : 1;
                }
                if (a[sortby] > b[sortby]) {
                    return order === 'ASC' ? 1 : -1;
                }
                return 0;
            });
        },
        [values]
    );

    const filterDataSubject = (dataSubject) => {
        if (!values?.search) return sortDataSubject(dataSubject);
        // to lower case
        const search = values?.search.toLowerCase();
        const filter = dataSubject.filter((item) =>
            item?.categoryPersonalDataName.toLowerCase().includes(search)
        );
        return sortDataSubject(filter) || [];
    };

    useEffect(() => {
        if (params.get('id') && dataSubject.length > 0 && initUrl) {
            const dataSubjectId = dataSubject.find(
                (item) => item?.dataSubjectId === params.get('id')
            );
            if (!dataSubjectId) {
                navigate(
                    ROUTER.DATA_INQUIRY.DEFAULT +
                        `?id=${dataSubject[0]?.categoryPersonalDataId}#${TAB.PERSONAL}`
                );
                setInitUrl(false);
            }
        }
        if (!params.get('id') && dataSubject.length > 0) {
            navigate(
                ROUTER.DATA_INQUIRY.DEFAULT +
                    `?id=${dataSubject[0]?.categoryPersonalDataId}#${TAB.PERSONAL}`
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSubject, params.get('id'), initUrl]);

    const setColor = (type) => {
        switch (type) {
            case 'Personal Data':
                return COLOR_TYPE.PersonalData;
            case 'Sensitive Data':
                return COLOR_TYPE.SensitiveData;
            default:
                return COLOR_TYPE.general;
        }
    };

    // Set categoryPersonalDataType to personalDataTypes
    useEffect(() => {
        const type = dataSubject.map((item) => item?.categoryPersonalDataType);
        const uniqueType = [...new Set(type)];
        const result = uniqueType.map((item) => {
            return {
                label: item,
                value: item,
            };
        });
        setPersonalDataTypes([personalDataTypesInit[0], ...result]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSubject, i18n.language]);

    const getAllIdsFormCategory = useCallback(
        (id) => {
            let payload = {
                personalDataIds: '',
                sensitiveDataIds: '',
                topicDataIds: '',
            };
            const find = dataSubject.find((item) =>
                item.categoryPersonalDataIds.includes(id)
            );
            if (find) {
                const ids = find?.categoryPersonalDataIds;
                if (
                    find?.categoryPersonalDataType ===
                    PERSONAL_TYPE.PersonalData
                ) {
                    payload.personalDataIds = ids;
                    payload.sensitiveDataIds = '';
                    payload.topicDataIds = '';
                } else if (
                    find?.categoryPersonalDataType ===
                    PERSONAL_TYPE.SensitiveData
                ) {
                    payload.personalDataIds = '';
                    payload.sensitiveDataIds = ids;
                    payload.topicDataIds = '';
                } else {
                    payload.personalDataIds = '';
                    payload.sensitiveDataIds = '';
                    payload.topicDataIds = ids;
                }
            }
            return payload;
        },
        [dataSubject]
    );

    const handleExport = () => {
        // Get All dataSubjectIds from checkedList with getAllIdsFormCategory
        const allIds = checkedList.map((id) => getAllIdsFormCategory(id));
        const personalDataIds = allIds
            .map((item) => item.personalDataIds)
            .filter((item) => item !== '')
            .join(',');
        const sensitiveDataIds = allIds
            .map((item) => item.sensitiveDataIds)
            .filter((item) => item !== '')
            .join(',');
        const topicDataIds = allIds
            .map((item) => item.topicDataIds)
            .filter((item) => item !== '')
            .join(',');

        exportDataFlowRopa({
            personalDataIds,
            sensitiveDataIds,
            topicDataIds,
            departmentId,
        });
    };

    return (
        <div className="bg-white col-span-2 rounded-lg h-max">
            <Form
                layout="vertical"
                form={form}
                className="px-4 pt-4"
                onValuesChange={(_, value) => setValues(value)}
                initialValues={{
                    sortby: ['categoryPersonalDataName', 'ASC'],
                    personalType: 'all',
                    search: '',
                }}
            >
                <FormItem
                    label={
                        <span className="font-semibold text-base">
                            {t('Search Filter')}
                        </span>
                    }
                    name="sortby"
                    className="mb-2"
                >
                    <Cascader
                        className="w-full"
                        allowClear={false}
                        size="large"
                        options={optionSortby}
                        showArrow
                    />
                </FormItem>
                <FormItem name="personalType" className="mb-2">
                    <Select
                        size="large"
                        options={personalDataTypes}
                        className="w-full"
                        filterOption={(input, option) =>
                            (option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        showSearch
                    />
                </FormItem>
                <FormItem name="search" noStyle>
                    <Input
                        allowClear
                        placeholder={t('Search for personal data keywords')}
                        size="large"
                        prefix={<SearchOutlined />}
                        onChange={(e) => {
                            setCheckedList([]);
                            setIndeterminate(false);
                            setCheckAll(false);
                        }}
                    />
                </FormItem>
            </Form>
            <Divider />
            <div className="px-4 pb-4">
                <div className="font-semibold text-xl">
                    {t('Personal Data')}
                </div>
                <div className="flex justify-between items-center mt-4">
                    <div className="flex space-x-2 items-center">
                        <Checkbox
                            indeterminate={indeterminate}
                            onChange={onCheckAllChange}
                            checked={checkAll}
                            className="font-semibold"
                        >
                            {t('Select All')} ({checkedList?.length})
                        </Checkbox>
                        <Button
                            loading={loadingExport}
                            onClick={handleExport}
                            disabled={!checkedList.length > 0}
                            type="primary"
                            icon={<DownloadOutlined />}
                        />
                    </div>
                    <div className="font-semibold text-gray-400">
                        {t('TotalItems', {
                            length: filterDataSubject(dataSubject)?.length,
                        })}
                    </div>
                </div>
            </div>
            <div
                className="h-[60vh] overflow-auto overflow-x-hidden relative"
                style={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                }}
            >
                <Spin spinning={loading}>
                    <CheckboxGroup
                        value={checkedList}
                        onChange={onChange}
                        className="w-full"
                    >
                        {filterDataSubject(dataSubject).map((item) => {
                            const active =
                                params.get('id') ===
                                item?.categoryPersonalDataId;
                            return (
                                <div
                                    key={item?.categoryPersonalDataId}
                                    className={classNames(
                                        'hover:bg-[#F8F4FF] duration-300 w-full relative flex items-center',
                                        {
                                            'bg-[#F8F4FF]': active,
                                        }
                                    )}
                                    style={{
                                        borderBottom:
                                            '1px solid rgba(0, 0, 0, 0.06)',
                                    }}
                                >
                                    {active && (
                                        <div className="w-[6px] h-full bg-purple-PHX absolute left-0 top-0 rounded-tr-lg rounded-br-lg" />
                                    )}
                                    <Checkbox
                                        className="p-4"
                                        value={item?.categoryPersonalDataId}
                                    ></Checkbox>
                                    <button
                                        className="text-left w-full py-4"
                                        onClick={() => {
                                            navigate(
                                                ROUTER.DATA_INQUIRY.DEFAULT +
                                                    `?id=${item?.categoryPersonalDataId}#${TAB.PERSONAL}`
                                            );
                                        }}
                                        type="button"
                                        key={item?.categoryPersonalDataId}
                                    >
                                        <div className="font-semibold">
                                            {item?.categoryPersonalDataName}
                                        </div>
                                        <Tag
                                            color={setColor(
                                                item?.categoryPersonalDataType
                                            )}
                                            className="rounded-full"
                                        >
                                            {item?.categoryPersonalDataType}
                                        </Tag>
                                    </button>
                                </div>
                            );
                        })}
                    </CheckboxGroup>
                </Spin>
            </div>
        </div>
    );
}
